import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Iconify from "../../iconify/Iconify";

const DEFAULT_SEPA_DIRECT_DEBIT_CONSENT_TEXT =
  "Ich ermächtige den Veranstalter, den fälligen Betrag per SEPA-Lastschrift " +
  "von meinem Konto einzuziehen. Zugleich weise ich mein Kreditinstitut an, " +
  "die vom Veranstalter auf mein Konto gezogenen Lastschriften einzulösen. " +
  "Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, " +
  "die Erstattung des belasteten Betrags verlangen. Es gelten dabei die mit " +
  "meinem Kreditinstitut vereinbarten Bedingungen.";
const DEFAULT_PUBLIC_BOOKING_CONFIRMATION_TEXT =
  "Hallo {{bookerFirstName}} {{bookerLastName}},\n\n" +
  "vielen Dank für Ihre Anmeldung zu \"{{eventName}}\".\n" +
  "Ihre Buchungsnummer lautet: {{registrationNumber}}.\n" +
  "Status Ihrer Anmeldung: {{registrationStatus}}.\n\n" +
  "Ihre Buchungsdaten:\n" +
  "- Teilnehmer: {{participantNames}}\n" +
  "- Sessions: {{sessionLabels}}\n" +
  "- Gesamtbetrag: {{totalAmount}}\n" +
  "- Zahlungsart: {{paymentMethod}}\n\n" +
  "Zahlungsinformationen:\n" +
  "{{paymentText}}\n\n" +
  "Link zu Ihrer Anmeldung: {{editLink}}\n\n" +
  "Viele Grüße\n" +
  "{{tenantName}}";

const DEFAULT_PUBLIC_BOOKING_CONFIRMATION_HTML_TEMPLATE =
  "<!DOCTYPE html>\n" +
  '<html lang="de">\n' +
  "<head>\n" +
  '  <meta charset="UTF-8" />\n' +
  '  <meta name="viewport" content="width=device-width, initial-scale=1.0" />\n' +
  "  <title>Anmeldebestätigung</title>\n" +
  "</head>\n" +
  '<body style="margin:0;padding:0;background:#f3f5f9;font-family:Arial,Helvetica,sans-serif;color:#18222f;">\n' +
  '  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f3f5f9;">\n' +
  "    <tr>\n" +
  '      <td align="center" style="padding:24px 12px;">\n' +
  '        <table role="presentation" width="620" cellpadding="0" cellspacing="0" style="max-width:620px;width:100%;background:#ffffff;border-radius:14px;overflow:hidden;border:1px solid #e6eaf1;">\n' +
  "          <!--\n" +
  "            Verfügbare Platzhalter (ohne Leerzeichen verwenden):\n" +
  "            {{tenantName}}           Name des Studios/Mandanten\n" +
  "            {{eventName}}            Veranstaltungstitel\n" +
  "            {{eventDate}}            Datum der Veranstaltung\n" +
  "            {{eventLocation}}        Veranstaltungsort\n" +
  "            {{registrationNumber}}   Buchungsnummer\n" +
  "            {{registrationStatus}}   Status der Anmeldung\n" +
  "            {{bookerFirstName}}      Vorname des Buchenden\n" +
  "            {{bookerLastName}}       Nachname des Buchenden\n" +
  "            {{bookerEmail}}          E-Mail des Buchenden\n" +
  "            {{participantNames}}     Teilnehmer (kommagetrennt)\n" +
  "            {{participantCount}}     Anzahl Teilnehmer\n" +
  "            {{sessionLabels}}        Gewählte Sessions\n" +
  "            {{totalAmount}}          Gesamtbetrag\n" +
  "            {{paymentMethod}}        Zahlungsart\n" +
  "            {{paymentText}}          Zahlungsblock inkl. Verwendungszweck und bei Überweisung\n" +
  "                                     auch Empfänger/IBAN/BIC, falls Banking-Daten vorhanden sind\n" +
  "            {{paymentAmount}}        Betrag als Zahlungsbetrag\n" +
  "            {{paymentReference}}     Verwendungszweck\n" +
  "            {{paymentRecipient}}     Empfänger (falls vorhanden)\n" +
  "            {{paymentIban}}          IBAN (falls vorhanden)\n" +
  "            {{paymentBic}}           BIC (falls vorhanden)\n" +
  "            {{paymentCustomText}}    Zusatztext aus Event-Einstellungen\n" +
  "            {{editLink}}             Link zur Anmeldung\n" +
  "          -->\n" +
  '          <tr><td style="padding:22px 24px;background:linear-gradient(135deg,#0f2742 0%,#18508a 100%);color:#fff;">\n' +
  '            <div style="font-size:13px;opacity:0.92;">Anmeldung bestätigt</div>\n' +
  '            <h1 style="margin:8px 0 0;font-size:22px;line-height:1.2;">{{eventName}}</h1>\n' +
  "          </td></tr>\n" +
  '          <tr><td style="padding:24px;">\n' +
  '            <p style="margin:0 0 14px;font-size:16px;">Hallo {{bookerFirstName}} {{bookerLastName}},</p>\n' +
  '            <p style="margin:0 0 16px;font-size:15px;line-height:1.55;">vielen Dank für Ihre Anmeldung bei <strong>{{tenantName}}</strong>.</p>\n' +
  '            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;background:#f8fafc;border:1px solid #e6eaf1;border-radius:10px;overflow:hidden;">\n' +
  '              <tr><td style="padding:12px 14px;border-bottom:1px solid #e6eaf1;"><strong>Buchungsnummer</strong><br/>{{registrationNumber}}</td></tr>\n' +
  '              <tr><td style="padding:12px 14px;border-bottom:1px solid #e6eaf1;"><strong>Status</strong><br/>{{registrationStatus}}</td></tr>\n' +
  '              <tr><td style="padding:12px 14px;border-bottom:1px solid #e6eaf1;"><strong>Teilnehmer</strong><br/>{{participantNames}}</td></tr>\n' +
  '              <tr><td style="padding:12px 14px;border-bottom:1px solid #e6eaf1;"><strong>Sessions</strong><br/>{{sessionLabels}}</td></tr>\n' +
  '              <tr><td style="padding:12px 14px;"><strong>Gesamtbetrag / Zahlungsart</strong><br/>{{totalAmount}} / {{paymentMethod}}</td></tr>\n' +
  "            </table>\n" +
  '            <div style="margin-top:16px;padding:14px;background:#eef7ff;border:1px solid #cfe5ff;border-radius:10px;">\n' +
  '              <div style="font-size:14px;font-weight:700;margin-bottom:8px;">Zahlungsinformationen</div>\n' +
  '              <div style="font-size:14px;line-height:1.55;white-space:pre-line;">{{paymentText}}</div>\n' +
  "            </div>\n" +
  '            <div style="margin-top:22px;">\n' +
  '              <a href="{{editLink}}" style="display:inline-block;background:#18508a;color:#ffffff;text-decoration:none;padding:11px 16px;border-radius:8px;font-weight:700;">Anmeldung ansehen</a>\n' +
  "            </div>\n" +
  '            <p style="margin:18px 0 0;font-size:14px;color:#526074;">Falls der Button nicht funktioniert, nutzen Sie diesen Link:<br/><span style="word-break:break-all;">{{editLink}}</span></p>\n' +
  "          </td></tr>\n" +
  '          <tr><td style="padding:16px 24px;background:#f8fafc;color:#6f7e8f;font-size:12px;line-height:1.4;">\n' +
  "            Diese E-Mail wurde automatisch von {{tenantName}} versendet.\n" +
  "          </td></tr>\n" +
  "        </table>\n" +
  "      </td>\n" +
  "    </tr>\n" +
  "  </table>\n" +
  "</body>\n" +
  "</html>";

const PUBLIC_BOOKING_TEMPLATE_MODES = {
  TEXT: "text",
  HTML: "html",
};

const PUBLIC_CONFIRMATION_PLACEHOLDERS = [
  { token: "{{tenantName}}", description: "Name Ihres Mandanten/Studios" },
  { token: "{{eventName}}", description: "Titel der Veranstaltung" },
  { token: "{{eventDate}}", description: "Datum der Veranstaltung (deutsches Format)" },
  { token: "{{eventLocation}}", description: "Ort der Veranstaltung" },
  { token: "{{registrationNumber}}", description: "Buchungsnummer der Anmeldung" },
  { token: "{{registrationStatus}}", description: "Aktueller Buchungsstatus" },
  { token: "{{bookerFirstName}}", description: "Vorname des Buchenden" },
  { token: "{{bookerLastName}}", description: "Nachname des Buchenden" },
  { token: "{{bookerEmail}}", description: "E-Mail-Adresse des Buchenden" },
  { token: "{{participantNames}}", description: "Kommagetrennte Teilnehmernamen" },
  { token: "{{participantCount}}", description: "Anzahl der Teilnehmer" },
  { token: "{{sessionLabels}}", description: "Gewählte Sessions" },
  { token: "{{totalAmount}}", description: "Gesamtbetrag der Buchung" },
  { token: "{{paymentMethod}}", description: "Zahlungsart (SEPA/Überweisung)" },
  {
    token: "{{paymentText}}",
    description:
      "Automatischer Zahlungsblock inkl. Betrag, Verwendungszweck und bei Überweisung auch Empfänger/IBAN/BIC, falls Banking-Daten vorhanden sind",
  },
  { token: "{{paymentAmount}}", description: "Betrag als Zahlbetrag" },
  { token: "{{paymentReference}}", description: "Verwendungszweck zur Zahlung" },
  { token: "{{paymentRecipient}}", description: "Empfängername bei Überweisung (falls vorhanden)" },
  { token: "{{paymentIban}}", description: "IBAN bei Überweisung (falls vorhanden)" },
  { token: "{{paymentBic}}", description: "BIC bei Überweisung (falls vorhanden)" },
  { token: "{{paymentCustomText}}", description: "Zusatztext aus 'Zahlungsinformationen'" },
  { token: "{{editLink}}", description: "Magic-Link zur Ansicht der Anmeldung" },
];

const buildClientId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyCustomField = () => ({
  fieldId: buildClientId("field"),
  label: "",
  scope: "participant",
  type: "text",
  placeholder: "",
  options: [],
  optionsInput: "",
  required: false,
  sensitive: false,
  onlyForChildren: false,
  showCondition: {
    dependsOnFieldId: "",
    dependsOnValue: "",
  },
});

const createEmptyConsent = () => ({
  consentId: buildClientId("consent"),
  title: "",
  text: "",
  required: true,
});

const joinOptions = (options) => {
  if (!Array.isArray(options)) return "";
  return options.join(", ");
};

const splitOptions = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

export default function StepExtras({ formData, errors, onChange }) {
  const registrationConfig = formData.registrationConfig || {};
  const isPublicBookingEnabled =
    Boolean(formData.isPublic) &&
    Boolean(registrationConfig.isPublicRegistrationEnabled);
  const customFields = Array.isArray(registrationConfig.customFields)
    ? registrationConfig.customFields
    : [];
  const requiredConsents = Array.isArray(registrationConfig.requiredConsents)
    ? registrationConfig.requiredConsents
    : [];
  const bankTransferInfo = registrationConfig.bankTransferInfo || {};
  const sepaDirectDebitConsentText =
    typeof registrationConfig.sepaDirectDebitConsentText === "string"
      ? registrationConfig.sepaDirectDebitConsentText
      : DEFAULT_SEPA_DIRECT_DEBIT_CONSENT_TEXT;
  const publicBookingConfirmationText =
    typeof registrationConfig.publicBookingConfirmationText === "string"
      ? registrationConfig.publicBookingConfirmationText
      : DEFAULT_PUBLIC_BOOKING_CONFIRMATION_TEXT;
  const publicBookingConfirmationTemplateMode =
    registrationConfig.publicBookingConfirmationTemplateMode ===
    PUBLIC_BOOKING_TEMPLATE_MODES.HTML
      ? PUBLIC_BOOKING_TEMPLATE_MODES.HTML
      : PUBLIC_BOOKING_TEMPLATE_MODES.TEXT;
  const publicBookingConfirmationHtmlTemplate =
    typeof registrationConfig.publicBookingConfirmationHtmlTemplate === "string"
      ? registrationConfig.publicBookingConfirmationHtmlTemplate
      : "";

  const updateRegistrationConfig = (patch) => {
    onChange("registrationConfig", {
      ...registrationConfig,
      ...patch,
    });
  };

  const updateCustomFields = (nextFields) => {
    const normalized = nextFields.map((field, index) => ({
      ...field,
      sortOrder: index,
    }));
    updateRegistrationConfig({ customFields: normalized });
  };

  const updateCustomField = (index, patch) => {
    const next = customFields.map((field, fieldIndex) =>
      fieldIndex === index ? { ...field, ...patch } : field
    );
    updateCustomFields(next);
  };

  const removeCustomField = (index) => {
    updateCustomFields(customFields.filter((_, fieldIndex) => fieldIndex !== index));
  };

  const addCustomField = () => {
    updateCustomFields([...customFields, createEmptyCustomField()]);
  };

  const updateRequiredConsents = (nextConsents) => {
    const normalized = nextConsents.map((consent, index) => ({
      ...consent,
      sortOrder: index,
    }));
    updateRegistrationConfig({ requiredConsents: normalized });
  };

  const updateConsent = (index, patch) => {
    const next = requiredConsents.map((consent, consentIndex) =>
      consentIndex === index ? { ...consent, ...patch } : consent
    );
    updateRequiredConsents(next);
  };

  const removeConsent = (index) => {
    updateRequiredConsents(
      requiredConsents.filter((_, consentIndex) => consentIndex !== index)
    );
  };

  const addConsent = () => {
    updateRequiredConsents([...requiredConsents, createEmptyConsent()]);
  };

  const customFieldChoices = customFields
    .map((field, index) => ({
      id: field.fieldId || `field-${index}`,
      label: field.label || `Feld ${index + 1}`,
    }))
    .filter((entry) => Boolean(entry.id));

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Extras</Typography>
        <Typography variant="body2" color="text.secondary">
          Verwalten Sie Zusatzfelder, Einwilligungen und Zahlungsinformationen.
        </Typography>
      </Box>

      {errors.customFields ? <Alert severity="error">{errors.customFields}</Alert> : null}
      {errors.requiredConsents ? (
        <Alert severity="error">{errors.requiredConsents}</Alert>
      ) : null}

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Zusatzfelder</Typography>

        {customFields.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2.5, borderStyle: "dashed" }}>
            <Typography variant="body2" color="text.secondary">
              Noch keine Zusatzfelder angelegt.
            </Typography>
          </Paper>
        ) : (
          customFields.map((field, index) => {
            const key = field.fieldId || field._id || `field-${index}`;
            const usesOptions = ["select", "multiselect"].includes(field.type);
            const showCondition = field.showCondition || {};
            const dependentFieldId = showCondition.dependsOnFieldId || "";
            const dependentValue = showCondition.dependsOnValue || "";

            return (
              <Paper key={key} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Feld {index + 1}</Typography>
                    <Tooltip title="Feld entfernen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeCustomField(index)}
                      >
                        <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      label="Bezeichnung"
                      value={field.label || ""}
                      onChange={(event) =>
                        updateCustomField(index, { label: event.target.value })
                      }
                      required
                      fullWidth
                    />
                    <TextField
                      label="Geltungsbereich"
                      select
                      value={field.scope || "participant"}
                      onChange={(event) =>
                        updateCustomField(index, { scope: event.target.value })
                      }
                      sx={{ minWidth: { xs: "100%", md: 180 } }}
                    >
                      <MenuItem value="booking">Buchung</MenuItem>
                      <MenuItem value="participant">Teilnehmer</MenuItem>
                    </TextField>
                    <TextField
                      label="Typ"
                      select
                      value={field.type || "text"}
                      onChange={(event) =>
                        updateCustomField(index, {
                          type: event.target.value,
                          options: ["select", "multiselect"].includes(event.target.value)
                            ? Array.isArray(field.options)
                              ? field.options
                              : []
                            : [],
                          optionsInput: ["select", "multiselect"].includes(event.target.value)
                            ? field.optionsInput ?? joinOptions(field.options)
                            : "",
                        })
                      }
                      sx={{ minWidth: { xs: "100%", md: 180 } }}
                    >
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="number">Zahl</MenuItem>
                      <MenuItem value="date">Datum</MenuItem>
                      <MenuItem value="checkbox">Checkbox</MenuItem>
                      <MenuItem value="select">Auswahl</MenuItem>
                      <MenuItem value="multiselect">Mehrfachauswahl</MenuItem>
                    </TextField>
                  </Stack>

                  <TextField
                    label="Placeholder (optional)"
                    value={field.placeholder || ""}
                    onChange={(event) =>
                      updateCustomField(index, {
                        placeholder: event.target.value,
                      })
                    }
                    fullWidth
                  />

                  {usesOptions ? (
                    <TextField
                      label="Optionen (kommagetrennt)"
                      value={field.optionsInput ?? joinOptions(field.options)}
                      onChange={(event) =>
                        updateCustomField(index, {
                          optionsInput: event.target.value,
                          options: splitOptions(event.target.value),
                        })
                      }
                      helperText="Beispiel: Vegetarisch, Vegan, Ohne Schweinefleisch"
                      fullWidth
                    />
                  ) : null}

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.required)}
                          onChange={(event) =>
                            updateCustomField(index, {
                              required: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Pflichtfeld"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.sensitive)}
                          onChange={(event) =>
                            updateCustomField(index, {
                              sensitive: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Sensible Daten"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.onlyForChildren)}
                          onChange={(event) =>
                            updateCustomField(index, {
                              onlyForChildren: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Nur für Kinder"
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      label="Sichtbar, wenn Feld"
                      select
                      value={dependentFieldId}
                      onChange={(event) =>
                        updateCustomField(index, {
                          showCondition: {
                            ...showCondition,
                            dependsOnFieldId: event.target.value,
                          },
                        })
                      }
                      fullWidth
                    >
                      <MenuItem value="">Keine Bedingung</MenuItem>
                      {customFieldChoices
                        .filter((entry) => entry.id !== field.fieldId)
                        .map((entry) => (
                          <MenuItem key={entry.id} value={entry.id}>
                            {entry.label}
                          </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                      label="erwarteter Wert"
                      value={dependentValue}
                      onChange={(event) =>
                        updateCustomField(index, {
                          showCondition: {
                            ...showCondition,
                            dependsOnValue: event.target.value,
                          },
                        })
                      }
                      disabled={!dependentFieldId}
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </Paper>
            );
          })
        )}

        <Box>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="solar:add-circle-bold" width={18} />}
            onClick={addCustomField}
          >
            Zusatzfeld hinzufügen
          </Button>
        </Box>
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Einwilligungen</Typography>

        {requiredConsents.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2.5, borderStyle: "dashed" }}>
            <Typography variant="body2" color="text.secondary">
              Keine Einwilligungen hinterlegt.
            </Typography>
          </Paper>
        ) : (
          requiredConsents.map((consent, index) => {
            const key =
              consent.consentId || consent._id || `consent-${index}`;
            return (
              <Paper key={key} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">Einwilligung {index + 1}</Typography>
                    <Tooltip title="Einwilligung entfernen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeConsent(index)}
                      >
                        <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <TextField
                    label="Titel"
                    value={consent.title || ""}
                    onChange={(event) =>
                      updateConsent(index, {
                        title: event.target.value,
                      })
                    }
                    required
                    fullWidth
                  />

                  <TextField
                    label="Text"
                    multiline
                    minRows={3}
                    value={consent.text || ""}
                    onChange={(event) =>
                      updateConsent(index, {
                        text: event.target.value,
                      })
                    }
                    fullWidth
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={consent.required !== false}
                        onChange={(event) =>
                          updateConsent(index, {
                            required: event.target.checked,
                          })
                        }
                      />
                    }
                    label="Pflicht-Einwilligung"
                  />
                </Stack>
              </Paper>
            );
          })
        )}

        <Box>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="solar:add-circle-bold" width={18} />}
            onClick={addConsent}
          >
            Einwilligung hinzufügen
          </Button>
        </Box>
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Zahlungsinformationen</Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(bankTransferInfo.enabled)}
                  onChange={(event) =>
                    updateRegistrationConfig({
                      bankTransferInfo: {
                        ...bankTransferInfo,
                        enabled: event.target.checked,
                      },
                    })
                  }
                  color="secondary"
                />
              }
              label="Überweisung als Zahlungsweg anzeigen"
            />

            <TextField
              label="Zusatztext zur Zahlungsinformation"
              multiline
              minRows={3}
              value={bankTransferInfo.customText || ""}
              onChange={(event) =>
                updateRegistrationConfig({
                  bankTransferInfo: {
                    ...bankTransferInfo,
                    customText: event.target.value,
                  },
                })
              }
              helperText="Wird in Buchungsbestätigung und öffentlicher Seite zusätzlich angezeigt."
              fullWidth
            />

            <TextField
              label="SEPA-Zustimmungstext"
              multiline
              minRows={4}
              value={sepaDirectDebitConsentText}
              onChange={(event) =>
                updateRegistrationConfig({
                  sepaDirectDebitConsentText: event.target.value,
                })
              }
              helperText="Dieser Text wird im öffentlichen Formular angezeigt, sobald SEPA-Lastschrift ausgewählt wird."
              fullWidth
            />
          </Stack>
        </Paper>
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">
          Bestätigungsmail bei öffentlicher Anmeldung
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {isPublicBookingEnabled ? (
              <Alert severity="info">
                Nach jeder öffentlichen Anmeldung wird automatisch eine
                Bestätigungsmail an den Buchenden versendet.
              </Alert>
            ) : (
              <Alert severity="warning">
                Dieser Text wird erst verwendet, wenn das Event öffentlich ist
                und die öffentliche Anmeldung aktiviert wurde.
              </Alert>
            )}

            <Alert severity="info">
              Der Platzhalter <strong>{"{{paymentText}}"}</strong> erzeugt
              automatisch einen fertigen Zahlungsblock. Bei Überweisung enthält er
              neben Betrag und Verwendungszweck auch Empfänger, IBAN und BIC aus den
              hinterlegten Banking-Daten Ihres Tenants, falls Banking-Daten vorhanden
              sind.
            </Alert>

            <Alert severity="info">
              Es wird kein zusätzlicher Link automatisch angehängt. Wenn ein Link in
              der E-Mail stehen soll, verwenden Sie <strong>{"{{editLink}}"}</strong>.
            </Alert>

            <TextField
              label="Template-Modus"
              select
              value={publicBookingConfirmationTemplateMode}
              onChange={(event) =>
                updateRegistrationConfig({
                  publicBookingConfirmationTemplateMode: event.target.value,
                })
              }
              helperText="Text ist am einfachsten. HTML bietet volle Kontrolle für Layout und mobile Darstellung."
              sx={{ maxWidth: 360 }}
            >
              <MenuItem value={PUBLIC_BOOKING_TEMPLATE_MODES.TEXT}>Text</MenuItem>
              <MenuItem value={PUBLIC_BOOKING_TEMPLATE_MODES.HTML}>HTML</MenuItem>
            </TextField>

            {publicBookingConfirmationTemplateMode === PUBLIC_BOOKING_TEMPLATE_MODES.HTML ? (
              <>
                <Alert severity="warning">
                  Im HTML-Modus wird die Vorlage direkt verwendet. Bitte fügen Sie{" "}
                  <strong>{"{{editLink}}"}</strong> ein, wenn der Link enthalten sein soll.
                </Alert>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      updateRegistrationConfig({
                        publicBookingConfirmationHtmlTemplate:
                          DEFAULT_PUBLIC_BOOKING_CONFIRMATION_HTML_TEMPLATE,
                      })
                    }
                  >
                    Muster-HTML einsetzen
                  </Button>
                  <Button
                    variant="text"
                    onClick={() =>
                      updateRegistrationConfig({
                        publicBookingConfirmationHtmlTemplate: "",
                      })
                    }
                  >
                    HTML leeren
                  </Button>
                </Stack>

                <TextField
                  label="HTML-Vorlage für öffentliche Buchungsbestätigung"
                  multiline
                  minRows={16}
                  value={publicBookingConfirmationHtmlTemplate}
                  onChange={(event) =>
                    updateRegistrationConfig({
                      publicBookingConfirmationHtmlTemplate: event.target.value,
                    })
                  }
                  helperText="Nutzen Sie die Muster-Vorlage als Ausgangspunkt. Inline-CSS wird für E-Mail-Clients empfohlen."
                  fullWidth
                />
              </>
            ) : (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      updateRegistrationConfig({
                        publicBookingConfirmationText:
                          DEFAULT_PUBLIC_BOOKING_CONFIRMATION_TEXT,
                      })
                    }
                  >
                    Standard-Text einsetzen
                  </Button>
                </Stack>

                <TextField
                  label="Mailtext für öffentliche Buchungsbestätigung"
                  multiline
                  minRows={12}
                  value={publicBookingConfirmationText}
                  onChange={(event) =>
                    updateRegistrationConfig({
                      publicBookingConfirmationText: event.target.value,
                    })
                  }
                  helperText="Sie können freien Text und Platzhalter kombinieren."
                  fullWidth
                />
              </>
            )}

            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
              <Stack spacing={0.75}>
                <Typography variant="caption" color="text.secondary">
                  Verfügbare Platzhalter:
                </Typography>
                {PUBLIC_CONFIRMATION_PLACEHOLDERS.map((entry) => (
                  <Typography key={entry.token} variant="caption" color="text.secondary">
                    <strong>{entry.token}</strong> - {entry.description}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}

StepExtras.propTypes = {
  formData: PropTypes.shape({
    isPublic: PropTypes.bool,
    registrationConfig: PropTypes.shape({
      isPublicRegistrationEnabled: PropTypes.bool,
      customFields: PropTypes.array,
      requiredConsents: PropTypes.array,
      bankTransferInfo: PropTypes.shape({
        enabled: PropTypes.bool,
        customText: PropTypes.string,
      }),
      sepaDirectDebitConsentText: PropTypes.string,
      publicBookingConfirmationText: PropTypes.string,
      publicBookingConfirmationTemplateMode: PropTypes.oneOf(["text", "html"]),
      publicBookingConfirmationHtmlTemplate: PropTypes.string,
    }),
  }).isRequired,
  errors: PropTypes.shape({
    customFields: PropTypes.string,
    requiredConsents: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
